import {
  HCPFlowEnum,
  IHCPFlowKanbanColumn,
  IHCPFlowKanbanResponse,
  IHCPFlowSheetRule,
  IHCPFlowStatus,
} from './hcp-flow.interface'
import { applyLogisticsColumnLayout } from './hcp-flow-logistics-columns'
import { applySheetColumnLayout } from './hcp-flow-sheet-columns'
import {
  IHCPFlowHistory,
  IHCPFlowKanbanCard,
  IHCPFlowLogisticsOrderItem,
  IHCPPreServiceOrderCardSummary,
  IHCPPreServiceOrderGroupedItem,
  IHCPFlowKanbanCardProductMultiVariant,
} from './hcp-flow-card.interface'
import { extractHistoryActor } from './hcp-flow-history.helpers'

export const AWAITING_ART_DEFAULT_STATUSES: Array<Pick<IHCPFlowStatus, 'sequence' | 'name'>> = [
  { sequence: 1, name: 'Aguardando Briefing' },
  { sequence: 2, name: 'Revisão Interna' },
  { sequence: 3, name: 'Briefing Reprovado' },
  { sequence: 4, name: 'Produzindo Arte' },
  { sequence: 5, name: 'Revisão do Cliente' },
  { sequence: 6, name: 'Análise de Alteração' },
  { sequence: 7, name: 'Alteração em Andamento' },
  { sequence: 8, name: 'Aprovado (final)' },
  { sequence: 9, name: 'Arquivado' },
]

export const LOGISTICS_DEFAULT_STATUSES: Array<Pick<IHCPFlowStatus, 'sequence' | 'name'>> = [
  { sequence: 1, name: 'Aguardando separação' },
  { sequence: 2, name: 'Em separação' },
  { sequence: 3, name: 'A faturar' },
  { sequence: 4, name: 'Pronto para envio' },
  { sequence: 5, name: 'Enviado' },
  { sequence: 6, name: 'Entregue' },
]

const FLOW_DEFAULT_STATUSES: Partial<Record<HCPFlowEnum, Array<Pick<IHCPFlowStatus, 'sequence' | 'name'>>>> = {
  [HCPFlowEnum.AWAITING_ART]: AWAITING_ART_DEFAULT_STATUSES,
  [HCPFlowEnum.LOGISTICS]: LOGISTICS_DEFAULT_STATUSES,
}

const FLOW_DEFAULT_CODES: Partial<Record<HCPFlowEnum, number>> = {
  [HCPFlowEnum.AWAITING_ART]: 1,
  [HCPFlowEnum.LOGISTICS]: 5,
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asArray(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value as Record<string, unknown>[]
  return []
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value
  }
  return ''
}

function pickStringOrNumber(...values: unknown[]): string | number | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number' && !Number.isNaN(value)) return value
  }
  return null
}

function pickNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && !Number.isNaN(value)) return value
    if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value)
    }
  }
  return null
}

function pickBoolean(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') return value
  return fallback
}

function unwrapDeep(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload
  if (Array.isArray(payload)) return payload

  const record = payload as Record<string, unknown>

  if ('columns' in record || ('flow' in record && !('data' in record))) {
    return payload
  }

  if ('data' in record && record.data != null) {
    return unwrapDeep(record.data)
  }

  if ('result' in record && record.result != null) {
    return unwrapDeep(record.result)
  }

  return payload
}

function extractArray(payload: unknown, keys: string[]): Record<string, unknown>[] {
  const unwrapped = unwrapDeep(payload)

  if (Array.isArray(unwrapped)) {
    return asArray(unwrapped)
  }

  const record = asRecord(unwrapped)
  if (!record) return []

  for (const key of keys) {
    const value = record[key]
    if (Array.isArray(value)) return asArray(value)
  }

  return []
}

function parseSequenceFromDisplayCode(displayCode: string): number | null {
  const match = displayCode.match(/-(\d{1,2})$/)
  if (!match) return null
  return Number(match[1])
}

function resolveCardSequence(
  raw: Record<string, unknown>,
  hcpCurrentStatus: Record<string, unknown>,
  fallbackSequence?: number | null
): number {
  const displayCode = pickString(
    raw.hcpCurrentFlowDisplayCode,
    raw.hcp_current_flow_display_code,
    hcpCurrentStatus.displayCode,
    hcpCurrentStatus.display_code
  )

  return (
    pickNumber(
      raw.hcpCurrentFlowStatusSequence,
      raw.hcp_current_flow_status_sequence,
      raw.currentFlowStatusSequence,
      raw.current_flow_status_sequence,
      raw.statusSequence,
      raw.status_sequence,
      hcpCurrentStatus.sequence
    )
    ?? parseSequenceFromDisplayCode(displayCode)
    ?? fallbackSequence
    ?? 0
  )
}

function pickMaskField(
  record: Record<string, unknown> | null,
  field: 'family' | 'volumetry'
): { value: string; label: string } | null {
  if (!record) return null

  const directValue = pickString(record[`mask${field.charAt(0).toUpperCase()}${field.slice(1)}Value`])
  const directLabel = pickString(record[`mask${field.charAt(0).toUpperCase()}${field.slice(1)}Label`])
  if (directValue || directLabel) {
    return { value: directValue, label: directLabel || directValue }
  }

  const nested = asRecord(record[field]) ?? asRecord(record[`mask_${field}`])
  if (!nested) return null

  const value = pickString(nested.value, nested.code)
  const label = pickString(nested.label, nested.name, value)
  if (!value && !label) return null

  return { value: value || label, label: label || value }
}

function normalizeSheetRule(raw: Record<string, unknown>): IHCPFlowSheetRule | null {
  const sheetRule = asRecord(raw.sheetRule ?? raw.sheet_rule)
  const familyValue = pickString(
    sheetRule?.familyValue,
    sheetRule?.family_value,
    raw.familyValue,
    raw.family_value
  )
  const volumetryValue = pickString(
    sheetRule?.volumetryValue,
    sheetRule?.volumetry_value,
    raw.volumetryValue,
    raw.volumetry_value
  )
  const masterCode = pickString(
    sheetRule?.masterCode,
    sheetRule?.master_code,
    raw.masterCode,
    raw.master_code
  )

  if (!familyValue && !volumetryValue && !masterCode) return null

  return {
    familyValue: familyValue || null,
    volumetryValue: volumetryValue || null,
    masterCode: masterCode || null,
  }
}

function extractProductMask(product: Record<string, unknown>) {
  const maskCodeDetails = asRecord(product.maskCodeDetails ?? product.mask_code_details)
  const family = pickMaskField(maskCodeDetails, 'family')
    ?? pickMaskField(product, 'family')
  const volumetry = pickMaskField(maskCodeDetails, 'volumetry')
    ?? pickMaskField(product, 'volumetry')

  return { family, volumetry }
}

function normalizeProductMultiVariant(raw: Record<string, unknown> | null): IHCPFlowKanbanCardProductMultiVariant | null {
  if (!raw) return null

  const variantMask = extractProductMask(raw)

  return {
    id: raw.id as string | number | undefined,
    code: pickString(raw.code, raw.productCode, raw.product_code) || null,
    maskName: pickString(raw.maskName, raw.mask_name) || null,
    maskFamilyValue: variantMask.family?.value || null,
    maskFamilyLabel: variantMask.family?.label || null,
    maskVolumetryValue: variantMask.volumetry?.value || null,
    maskVolumetryLabel: variantMask.volumetry?.label || null,
  }
}

function pickVolumetry(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    const record = asRecord(value)
    if (record) {
      const label = pickString(record.label, record.name, record.value)
      if (label) return label
    }
  }
  return ''
}

function normalizeLogisticsOrderItems(raw: unknown): IHCPFlowLogisticsOrderItem[] {
  if (!Array.isArray(raw)) return []

  return raw
    .map((entry) => {
      const item = asRecord(entry)
      if (!item) return null

      const itemId = item.id ?? item.orderProductId ?? item.order_product_id
      if (itemId === undefined || itemId === null) return null

      const nestedProduct = asRecord(item.product) ?? {}

      return {
        id: typeof itemId === 'string' || typeof itemId === 'number' ? itemId : String(itemId),
        productName: pickString(item.productName, item.product_name, nestedProduct.name) || null,
        productOrderCode: pickString(item.productOrderCode, item.product_order_code) || null,
        totalQuantity: pickNumber(item.totalQuantity, item.total_quantity, item.quantity),
        volumetry: pickVolumetry(
          item.volumetry,
          item.volumetria,
          nestedProduct.volumetry,
          nestedProduct.volumetria
        ) || null,
        displayCode: pickString(
          item.displayCode,
          item.display_code,
          item.hcpCurrentFlowDisplayCode,
          item.hcp_current_flow_display_code
        ) || null,
        hcpCurrentFlowStatusName: pickString(
          item.hcpCurrentFlowStatusName,
          item.hcp_current_flow_status_name
        ) || null,
        serviceOrderId: pickNumber(item.serviceOrderId, item.service_order_id)
          ?? (typeof item.serviceOrderId === 'string' ? item.serviceOrderId : null)
          ?? (typeof item.service_order_id === 'string' ? item.service_order_id : null),
      } as IHCPFlowLogisticsOrderItem
    })
    .filter(Boolean) as IHCPFlowLogisticsOrderItem[]
}

function normalizeGroupedPreServiceOrderItems(raw: unknown): IHCPPreServiceOrderGroupedItem[] {
  if (!Array.isArray(raw)) return []

  return raw
    .map((entry) => {
      const item = asRecord(entry)
      if (!item) return null

      const itemId = item.id ?? item.orderProductId ?? item.order_product_id
      if (itemId === undefined || itemId === null) return null
      const order = asRecord(item.order) ?? {}
      const customer = asRecord(item.customer) ?? asRecord(order.customer) ?? {}

      return {
        id: typeof itemId === 'string' || typeof itemId === 'number' ? itemId : String(itemId),
        productName: pickString(item.productName, item.product_name) || null,
        productOrderCode: pickString(item.productOrderCode, item.product_order_code) || null,
        orderId: pickStringOrNumber(item.orderId, item.order_id, order.id),
        nsOrderId: pickStringOrNumber(
          item.nsOrderId,
          item.ns_order_id,
          item.orderNumber,
          item.order_number,
          order.nsOrderId,
          order.ns_order_id,
          order.orderId,
          order.order_id
        ),
        customerName: pickString(
          item.customerName,
          item.customer_name,
          customer.name,
          order.customerName,
          order.customer_name
        ) || null,
        totalQuantity: pickNumber(item.totalQuantity, item.total_quantity),
        orderQuantity: pickNumber(item.orderQuantity, item.order_quantity),
        hcpCurrentFlow: pickString(item.hcpCurrentFlow, item.hcp_current_flow) || null,
        hcpCurrentFlowStatusSequence: pickNumber(
          item.hcpCurrentFlowStatusSequence,
          item.hcp_current_flow_status_sequence
        ),
        hcpCurrentFlowDisplayCode: pickString(
          item.hcpCurrentFlowDisplayCode,
          item.hcp_current_flow_display_code
        ) || null,
        hcpCurrentFlowStatusName: pickString(
          item.hcpCurrentFlowStatusName,
          item.hcp_current_flow_status_name
        ) || null,
        hcpFinished: typeof item.hcpFinished === 'boolean'
          ? item.hcpFinished
          : typeof item.hcp_finished === 'boolean'
            ? item.hcp_finished
            : null,
      } as IHCPPreServiceOrderGroupedItem
    })
    .filter(Boolean) as IHCPPreServiceOrderGroupedItem[]
}

function normalizePreServiceOrderSummary(raw: unknown): IHCPPreServiceOrderCardSummary | null {
  const record = asRecord(raw)
  if (!record) return null

  const id = pickString(record.id, record.hcpPreServiceOrderId, record.hcp_pre_service_order_id)
  const number = pickNumber(record.number)
  if (!id || number === null) return null

  return {
    id,
    number,
    externalOsNumber: pickString(record.externalOsNumber, record.external_os_number) || null,
    status: pickString(record.status) || 'EMITTED',
    isActive: pickBoolean(record.isActive ?? record.is_active, true),
    currentFlow: pickString(record.currentFlow, record.current_flow) || null,
    currentFlowStatusId: pickString(record.currentFlowStatusId, record.current_flow_status_id) || null,
    currentFlowStatusSequence: pickNumber(
      record.currentFlowStatusSequence,
      record.current_flow_status_sequence
    ),
    currentFlowDisplayCode: pickString(record.currentFlowDisplayCode, record.current_flow_display_code) || null,
    currentFlowStatusName: pickString(record.currentFlowStatusName, record.current_flow_status_name) || null,
    currentFlowDeadline: pickString(record.currentFlowDeadline, record.current_flow_deadline) || null,
    emittedAt: pickString(record.emittedAt, record.emitted_at) || null,
    canceledAt: pickString(record.canceledAt, record.canceled_at) || null,
  }
}

function pickBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

function normalizeFlowHistories(raw: unknown): IHCPFlowHistory[] {
  if (!Array.isArray(raw)) return []

  return raw
    .map((entry) => {
      const item = asRecord(entry)
      if (!item) return null

      const actor = extractHistoryActor(item)
      const sequence = pickNumber(item.sequence, item.statusSequence, item.status_sequence)

      return {
        id: pickStringOrNumber(item.id) ?? null,
        orderProductId: pickStringOrNumber(item.orderProductId, item.order_product_id) ?? null,
        hcpFlowStatusId: pickString(item.hcpFlowStatusId, item.hcp_flow_status_id) || null,
        flow: pickString(item.flow, item.hcpCurrentFlow, item.hcp_current_flow) || null,
        flowCode: pickNumber(item.flowCode, item.flow_code),
        statusName: pickString(
          item.statusName,
          item.status_name,
          item.hcpCurrentFlowStatusName,
          item.hcp_current_flow_status_name,
          item.name
        ) || null,
        displayCode: pickString(item.displayCode, item.display_code) || null,
        sequence,
        statusSequence: sequence,
        createdAt: pickString(item.createdAt, item.created_at) || null,
        updatedAt: pickString(item.updatedAt, item.updated_at) || null,
        startedAt: pickString(item.startedAt, item.started_at) || null,
        deadlineAt: pickString(item.deadlineAt, item.deadline_at) || null,
        finishedAt: pickString(item.finishedAt, item.finished_at) || null,
        days: pickNumber(item.days),
        skipped: typeof item.skipped === 'boolean' ? item.skipped : null,
        skipReason: pickString(item.skipReason, item.skip_reason) || null,
        movedByName: actor.movedByName,
        movedByUsername: actor.movedByUsername,
        collaboratorName: actor.movedByName,
        collaboratorUsername: actor.movedByUsername,
      } as IHCPFlowHistory
    })
    .filter(Boolean) as IHCPFlowHistory[]
}

export function normalizeKanbanCard(
  raw: Record<string, unknown>,
  fallbackSequence?: number | null
): IHCPFlowKanbanCard | null {
  const order = asRecord(raw.order) ?? {}
  const replacementOrder = asRecord(raw.replacementOrder ?? raw.replacement_order)
  const isLogisticsOrderCard = pickBooleanFlag(
    raw.isLogisticsOrderCard ?? raw.is_logistics_order_card
  ) === true
  const cardType = pickString(raw.type).toUpperCase() === 'HCP_PRE_SERVICE_ORDER'
    ? 'HCP_PRE_SERVICE_ORDER'
    : 'ITEM'
  const preServiceOrder = normalizePreServiceOrderSummary(
    raw.hcpPreServiceOrder ?? raw.hcp_pre_service_order ?? raw.preServiceOrder ?? raw.pre_service_order
  )

  const rawId =
    raw.id ??
    raw.hcpPreServiceOrderId ??
    raw.hcp_pre_service_order_id ??
    raw.orderProductId ??
    raw.order_product_id ??
    (isLogisticsOrderCard ? order.id ?? order.orderId ?? order.order_id : null)

  if (rawId === undefined || rawId === null) return null

  const id = typeof rawId === 'string' || typeof rawId === 'number' ? rawId : String(rawId)
  const product = asRecord(raw.product) ?? {}
  const productMultiVariantRaw = asRecord(raw.productMultiVariant ?? raw.product_multi_variant)
  const productMultiVariant = normalizeProductMultiVariant(productMultiVariantRaw)
  const customer = asRecord(order.customer) ?? {}
  const hcpCurrentStatus = asRecord(raw.hcpCurrentStatus ?? raw.hcp_current_status) ?? {}

  const sequence = resolveCardSequence(
    raw,
    hcpCurrentStatus,
    preServiceOrder?.currentFlowStatusSequence ?? fallbackSequence
  )
  const displayCode = pickString(
    raw.displayCode,
    raw.display_code,
    raw.hcpCurrentFlowDisplayCode,
    raw.hcp_current_flow_display_code,
    raw.currentFlowDisplayCode,
    raw.current_flow_display_code,
    hcpCurrentStatus.displayCode,
    hcpCurrentStatus.display_code,
    preServiceOrder?.currentFlowDisplayCode
  )

  const totalQuantity = pickNumber(
    raw.totalQuantity,
    raw.total_quantity,
    raw.quantity
  )
  const productMask = extractProductMask(product)
  const volumetry = pickVolumetry(
    productMultiVariant?.maskVolumetryLabel,
    productMultiVariant?.maskVolumetryValue,
    productMask.volumetry?.label,
    productMask.volumetry?.value,
    raw.volumetry,
    raw.volumetria,
    raw.maskVolumetry,
    raw.mask_volumetry,
    product.volumetry,
    product.volumetria,
    product.maskVolumetry,
    product.mask_volumetry
  )

  return {
    id,
    type: cardType,
    hcpCurrentFlow: pickString(
      raw.hcpCurrentFlow,
      raw.hcp_current_flow,
      raw.currentFlow,
      raw.current_flow,
      hcpCurrentStatus.flow,
      preServiceOrder?.currentFlow
    ),
    hcpCurrentFlowStatusSequence: sequence,
    displayCode: displayCode || null,
    hcpCurrentFlowDisplayCode: displayCode || null,
    hcpCurrentFlowStatusName: pickString(
      raw.hcpCurrentFlowStatusName,
      raw.hcp_current_flow_status_name,
      raw.currentFlowStatusName,
      raw.current_flow_status_name,
      hcpCurrentStatus.name,
      preServiceOrder?.currentFlowStatusName
    ) || null,
    productOrderCode: pickString(raw.productOrderCode, raw.product_order_code) || null,
    customerDeadline: pickString(
      raw.customerDeadline,
      raw.customer_deadline,
      replacementOrder?.customerDeadline,
      replacementOrder?.customer_deadline
    ) || null,
    volumetry: volumetry || null,
    maskFamilyValue: productMask.family?.value || null,
    maskFamilyLabel: productMask.family?.label || null,
    totalQuantity,
    allArtsApproved: typeof raw.allArtsApproved === 'boolean'
      ? raw.allArtsApproved
      : typeof raw.all_arts_approved === 'boolean'
        ? raw.all_arts_approved
        : null,
    createdAt: pickString(raw.createdAt, raw.created_at) || null,
    updatedAt: pickString(raw.updatedAt, raw.updated_at) || null,
    hcpCurrentFlowStartedAt: pickString(
      raw.hcpCurrentFlowStartedAt,
      raw.hcp_current_flow_started_at
    ) || null,
    hcpCurrentFlowDeadline: pickString(
      raw.hcpCurrentFlowDeadline,
      raw.hcp_current_flow_deadline,
      raw.currentFlowDeadline,
      raw.current_flow_deadline,
      preServiceOrder?.currentFlowDeadline
    ) || null,
    hcpFinished: typeof raw.hcpFinished === 'boolean'
      ? raw.hcpFinished
      : typeof raw.hcp_finished === 'boolean'
        ? raw.hcp_finished
        : null,
    artLink: pickString(raw.artLink, raw.art_link) || null,
    serviceOrderId: pickNumber(raw.serviceOrderId, raw.service_order_id)
      ?? (typeof raw.serviceOrderId === 'string' ? raw.serviceOrderId : null)
      ?? (typeof raw.service_order_id === 'string' ? raw.service_order_id : null),
    hcpPreServiceOrderId: pickString(
      raw.hcpPreServiceOrderId,
      raw.hcp_pre_service_order_id,
      preServiceOrder?.id
    ) || null,
    hcpPreServiceOrder: preServiceOrder,
    groupedItemsCount: pickNumber(raw.groupedItemsCount, raw.grouped_items_count) ?? undefined,
    ordersCount: pickNumber(raw.ordersCount, raw.orders_count) ?? undefined,
    customersCount: pickNumber(raw.customersCount, raw.customers_count) ?? undefined,
    groupedItems: normalizeGroupedPreServiceOrderItems(raw.groupedItems ?? raw.grouped_items),
    hcpFlowHistories: normalizeFlowHistories(raw.hcpFlowHistories ?? raw.hcp_flow_histories),
    isLogisticsOrderCard,
    logisticsOrderItems: normalizeLogisticsOrderItems(
      raw.logisticsOrderItems ?? raw.logistics_order_items
    ),
    product: {
      id: product.id as string | number | undefined,
      name: pickString(
        product.name,
        raw.productName,
        raw.product_name
      ) || null,
      code: pickString(
        product.code,
        product.productCode,
        product.product_code,
        raw.productCode,
        raw.product_code
      ) || null,
      maskFamilyValue: productMask.family?.value || null,
      maskFamilyLabel: productMask.family?.label || null,
      maskVolumetryValue: productMask.volumetry?.value || null,
      maskVolumetryLabel: productMask.volumetry?.label || null,
    },
    productMultiVariant,
    order: {
      id: (order.id ?? raw.orderId ?? raw.order_id) as string | number | undefined,
      nsOrderId: pickNumber(
        order.nsOrderId,
        order.ns_order_id,
        order.orderId,
        raw.nsOrderId,
        raw.ns_order_id
      ) ?? null,
      customer: {
        name: pickString(
          customer.name,
          order.customerName,
          order.customer_name,
          raw.customerName,
          raw.customer_name
        ) || null,
        email: pickString(customer.email, raw.customerEmail, raw.customer_email) || null,
      },
    },
    replacementOrder: replacementOrder
      ? {
        id: pickStringOrNumber(replacementOrder.id) ?? undefined,
        status: pickString(replacementOrder.status) || null,
        productionStatus: pickString(
          replacementOrder.productionStatus,
          replacementOrder.production_status
        ) || null,
        store: pickString(replacementOrder.store) || null,
        identifier: pickString(replacementOrder.identifier) || null,
        brand: pickString(replacementOrder.brand) || null,
        dueDate: pickString(replacementOrder.dueDate, replacementOrder.due_date) || null,
        customerDeadline: pickString(
          replacementOrder.customerDeadline,
          replacementOrder.customer_deadline
        ) || null,
      }
      : null,
  }
}

export function normalizeFlowStatuses(payload: unknown, flow: HCPFlowEnum): IHCPFlowStatus[] {
  const rawStatuses = extractArray(payload, ['statuses', 'data', 'results'])

  const statuses = rawStatuses
    .map((status) => {
      const sequence = pickNumber(status.sequence, status.statusSequence, status.status_sequence)
      if (sequence === null) return null

      const id = pickString(status.id, status.statusId, status.status_id, `${flow}-${sequence}`)
      const sheetRule = normalizeSheetRule(status)

      return {
        id,
        flow: pickString(status.flow, flow),
        flowCode: pickNumber(status.flowCode, status.flow_code) ?? 1,
        name: pickString(status.name, status.statusName, status.status_name, `Status ${sequence}`),
        sequence,
        displayCode: pickString(
          status.displayCode,
          status.display_code,
          `${status.flowCode ?? status.flow_code ?? 1}-${String(sequence).padStart(2, '0')}`
        ),
        masterCode: pickString(status.masterCode, status.master_code, sheetRule?.masterCode) || null,
        isActive: pickBoolean(status.isActive ?? status.is_active, true),
        groupingMode: pickString(status.groupingMode, status.grouping_mode) === 'HCP_PRE_SERVICE_ORDER'
          ? 'HCP_PRE_SERVICE_ORDER'
          : 'ITEM',
        sheetRule,
      } as IHCPFlowStatus
    })
    .filter(Boolean) as IHCPFlowStatus[]

  statuses.sort((a, b) => a.sequence - b.sequence)

  const defaultStatuses = FLOW_DEFAULT_STATUSES[flow]
  const defaultFlowCode = FLOW_DEFAULT_CODES[flow] ?? statuses[0]?.flowCode ?? 1

  if (defaultStatuses && statuses.length < defaultStatuses.length) {
    const existingSequences = new Set(statuses.map((status) => status.sequence))

    defaultStatuses.forEach((fallback) => {
      if (existingSequences.has(fallback.sequence)) return

      statuses.push({
        id: `${flow}-${fallback.sequence}`,
        flow,
        flowCode: defaultFlowCode,
        name: fallback.name,
        sequence: fallback.sequence,
        displayCode: `${defaultFlowCode}-${String(fallback.sequence).padStart(2, '0')}`,
        isActive: true,
        groupingMode: 'ITEM',
      })
    })

    statuses.sort((a, b) => a.sequence - b.sequence)
  }

  return statuses
}

export function normalizeKanbanColumns(payload: unknown): IHCPFlowKanbanColumn[] {
  const rawColumns = extractArray(payload, ['columns', 'statuses', 'data', 'results'])

  return rawColumns
    .map((column) => {
      const sequence = pickNumber(column.sequence, column.statusSequence, column.status_sequence)
      if (sequence === null) return null

      const rawCards = extractArray(column, ['cards', 'items', 'orderProducts', 'order_products'])
      const sheetRule = normalizeSheetRule(column)

      return {
        statusId: pickString(column.statusId, column.status_id, column.id, `${column.flow}-${sequence}`),
        flow: pickString(column.flow),
        flowCode: pickNumber(column.flowCode, column.flow_code) ?? 1,
        name: pickString(column.name, column.statusName, column.status_name, `Status ${sequence}`),
        sequence,
        displayCode: pickString(column.displayCode, column.display_code)
          || `${column.flowCode ?? column.flow_code ?? 1}-${String(sequence).padStart(2, '0')}`,
        masterCode: pickString(column.masterCode, column.master_code, sheetRule?.masterCode) || null,
        isActive: pickBoolean(column.isActive ?? column.is_active, true),
        groupingMode: pickString(column.groupingMode, column.grouping_mode) === 'HCP_PRE_SERVICE_ORDER'
          ? 'HCP_PRE_SERVICE_ORDER'
          : 'ITEM',
        sheetRule,
        page: pickNumber(column.page) ?? 1,
        offset: pickNumber(column.offset) ?? 50,
        total: pickNumber(column.total, column.count) ?? rawCards.length,
        hasNextPage: pickBoolean(column.hasNextPage ?? column.has_next_page, false),
        cards: rawCards
          .map((card) => normalizeKanbanCard(card, sequence))
          .filter(Boolean) as IHCPFlowKanbanCard[],
      } as IHCPFlowKanbanColumn
    })
    .filter(Boolean) as IHCPFlowKanbanColumn[]
}

function buildColumnsFromStatuses(
  statuses: IHCPFlowStatus[],
  page: number,
  offset: number
): IHCPFlowKanbanColumn[] {
  return statuses
    .filter((status) => status.isActive !== false)
    .map((status) => ({
      statusId: status.id,
      flow: status.flow,
      flowCode: status.flowCode,
      sequence: status.sequence,
      displayCode: status.displayCode,
      name: status.name,
      masterCode: status.masterCode ?? null,
      isActive: status.isActive,
      groupingMode: status.groupingMode,
      sheetRule: status.sheetRule ?? null,
      page,
      offset,
      total: 0,
      hasNextPage: false,
      cards: [],
    }))
}

function enrichColumnsWithSheetRules(
  columns: IHCPFlowKanbanColumn[],
  statuses: IHCPFlowStatus[]
): IHCPFlowKanbanColumn[] {
  if (!statuses.length) return columns

  const statusById = new Map(statuses.map((status) => [status.id, status]))
  const statusBySequence = new Map(statuses.map((status) => [status.sequence, status]))
  const ruleByStatusId = new Map(
    statuses
      .filter((status) => status.sheetRule)
      .map((status) => [status.id, status.sheetRule])
  )
  const ruleBySequence = new Map(
    statuses
      .filter((status) => status.sheetRule)
      .map((status) => [status.sequence, status.sheetRule])
  )

  return columns.map((column) => ({
    ...column,
    masterCode: column.masterCode
      ?? statusById.get(column.statusId)?.masterCode
      ?? statusBySequence.get(column.sequence)?.masterCode
      ?? null,
    sheetRule: column.sheetRule
      ?? ruleByStatusId.get(column.statusId)
      ?? ruleBySequence.get(column.sequence)
      ?? null,
  }))
}

export function buildKanbanResponse(
  flow: HCPFlowEnum,
  page: number,
  offset: number,
  statusesPayload: unknown,
  kanbanPayload: unknown
): IHCPFlowKanbanResponse {
  const statuses = normalizeFlowStatuses(statusesPayload, flow)
  const kanbanRecord = asRecord(unwrapDeep(kanbanPayload))
  let columns = normalizeKanbanColumns(kanbanPayload)
  const hasKanbanColumns = columns.length > 0

  // Doc: o endpoint /kanban já retorna colunas (inclusive vazias).
  if (!hasKanbanColumns) {
    columns = buildColumnsFromStatuses(statuses, page, offset)
  } else {
    columns.sort((a, b) => a.sequence - b.sequence)
  }

  if (flow === HCPFlowEnum.SHEET) {
    columns = enrichColumnsWithSheetRules(columns, statuses)
    columns = applySheetColumnLayout(columns)
  }

  if (flow === HCPFlowEnum.LOGISTICS) {
    columns = applyLogisticsColumnLayout(columns, statuses, page, offset)
  }

  const totalCards = columns.reduce((acc, column) => acc + column.cards.length, 0)

  return {
    flow,
    page,
    offset,
    columns,
    total: totalCards,
    hasNextPage: columns.some((column) => column.hasNextPage),
    eligibleToGeneratePreOsCount: pickNumber(
      kanbanRecord?.eligibleToGeneratePreOsCount,
      kanbanRecord?.eligible_to_generate_pre_os_count
    ) ?? undefined,
  }
}

export function summarizeRawPayload(payload: unknown): string {
  const unwrapped = unwrapDeep(payload)
  if (Array.isArray(unwrapped)) return `array(${unwrapped.length})`
  const record = asRecord(unwrapped)
  if (!record) return typeof unwrapped

  const keys = Object.keys(record)
  const columns = extractArray(payload, ['columns', 'statuses', 'data', 'results'])
  return `object{${keys.join(',')}} columnsOrItems=${columns.length}`
}
